import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { assets } from '../constants/theme';

const sections = [
  {
    id: 'harvest',
    num: '01',
    title: 'The Harvest',
    subtitle: 'Where Quality Begins',
    icon: '🌿',
    color: '#2D6A2E',
    img: assets.imgFarm,
    content: [
      {
        heading: 'Selective Hand-Picking',
        text: 'On the volcanic slopes of Mt. Kenya, harvest season runs from October to December for the main crop. Our farmers — many of whom are third-generation growers — walk through rows of coffee trees, selecting only bright red cherries at peak ripeness. This selective picking process means multiple passes through the same trees over several weeks, ensuring only the best fruit is chosen.',
      },
      {
        heading: 'Quality at the Cherry',
        text: 'The difference between excellent coffee and mediocre coffee often starts right here. Underripe green cherries contribute harsh, astringent flavors. Overripe ones add fermented, rotten notes. By handpicking only perfectly ripe red cherries, our farmers are making quality decisions that no machine can replicate.',
      },
      {
        heading: 'Same-Day Processing',
        text: 'Freshly picked cherries are transported to the processing station within hours of harvest. In the intense equatorial heat, coffee cherries begin to ferment rapidly. Getting them processed quickly preserves the clean, bright flavors that define Kenyan specialty coffee.',
      },
    ],
  },
  {
    id: 'processing',
    num: '02',
    title: 'Processing Methods',
    subtitle: 'From Cherry to Green Bean',
    icon: '💧',
    color: '#1E5FA0',
    img: assets.imgProcess,
    content: [
      {
        heading: 'Washed / Wet Process (Kenyan Signature)',
        text: 'Our Kenyan AA and Arabica A grades undergo Kenya\'s renowned "double fermentation" washed process. Cherries are depulped immediately, then fermented in clean mountain spring water for 24–72 hours. This breaks down the mucilage (fruit layer) surrounding the bean and develops complex organic acids. After fermentation, the beans are washed in fresh water channels, then undergo a unique 24-hour "soaking" step that further elevates cup clarity and sweetness.',
      },
      {
        heading: 'Natural / Dry Process',
        text: 'In the natural process, whole coffee cherries are spread directly on raised drying beds without removing the fruit first. As the cherries dry over 3–6 weeks, the sugars and flavors from the fruit penetrate the bean, creating coffees with intense fruity, wine-like characteristics and exceptional body. Our natural lots are rotated every 30 minutes in the first few days to prevent mold.',
      },
      {
        heading: 'Honey / Pulped Natural Process',
        text: 'The honey process is a middle path: cherries are depulped but varying amounts of the sticky mucilage ("honey") are left on the bean during drying. Yellow honey (less mucilage) produces bright, clean cups. Red honey (more mucilage) creates sweeter, fuller cups. This method balances the best of washed clarity and natural sweetness.',
      },
    ],
  },
  {
    id: 'drying',
    num: '03',
    title: 'Drying & Milling',
    subtitle: 'Precision in Every Day',
    icon: '☀️',
    color: '#D96D29',
    img: 'https://images.unsplash.com/photo-1611854779393-1b2da9d400fe?w=1200&q=85',
    content: [
      {
        heading: 'African Raised Drying Beds',
        text: 'After washing, coffee is spread on mesh-topped raised beds — a critical Kenyan innovation. Elevating the beans off the ground allows air circulation on all sides, promoting even, slow drying. Workers turn the beans frequently during daylight hours using wooden rakes, monitoring moisture content throughout. This process takes 2–3 weeks depending on weather.',
      },
      {
        heading: 'Quality Control at Every Stage',
        text: 'During drying, dedicated quality control teams remove any defective beans, foreign matter, or irregularly drying lots. Moisture meters are used regularly — the target is 10.5–11.5% moisture content for stable, safe export. Rushing this stage is one of the most common sources of coffee defects.',
      },
      {
        heading: 'Hulling, Sorting & Grading',
        text: 'Once dried, parchment coffee is hulled to remove the outer layer. Beans then pass through density sorters, color sorters, and expert hand-sorting tables at the dry mill. Kenya\'s grading system classifies beans by size through a series of screens: AA (screen 18+, the largest), AB (17–18), C (below 17), and so on. Our Kenyan AA lots are certified by licensed graders.',
      },
    ],
  },
  {
    id: 'roasting',
    num: '04',
    title: 'Small-Batch Roasting',
    subtitle: 'The Art and Science of Heat',
    icon: '🔥',
    color: '#A63C1E',
    img: assets.roastingImg,
    content: [
      {
        heading: 'Temperature Profiling',
        text: 'Each coffee grade receives a custom roast profile developed over multiple sample roasts. For Kenyan AA, we use a slower, more developed profile that preserves the delicate fruit notes while avoiding harsh acidity. Arabica B Grade typically benefits from a slightly longer development time to extract its full chocolatey body. Data-logging software tracks every degree throughout the roast.',
      },
      {
        heading: 'Roast Levels & Development',
        text: 'We roast at three levels depending on the coffee and customer preference. Light roast (City to City+) preserves the most origin flavors — ideal for Kenyan AA\'s fruit-forward profile. Medium roast (Full City) adds caramelization and body while retaining complexity — our most popular for Arabica A. Medium-dark (Full City+) creates rich, intense cups with lower acidity — great for Arabica B blends.',
      },
      {
        heading: 'Degassing & Fresh Packaging',
        text: 'Freshly roasted coffee releases CO₂ for days after roasting. We rest roasted beans for 24–48 hours to allow degassing before packaging. Our bags include one-way degassing valves to allow CO₂ to escape without letting oxygen in — protecting your coffee\'s freshness for weeks.',
      },
    ],
  },
  {
    id: 'your-cup',
    num: '05',
    title: 'Your Perfect Cup',
    subtitle: 'The Ritual You Deserve',
    icon: '☕',
    color: '#8B4224',
    img: assets.imgBrew,
    content: [
      {
        heading: 'Brewing Methods Comparison',
        text: 'Different brewing methods extract different aspects of coffee\'s character. Pour over and Chemex highlight clarity, acidity, and subtle flavor complexity — perfect for Kenyan AA. French press uses full immersion, producing a fuller body with more oils — great for Arabica B. Espresso concentrates all flavors, making it excellent for Arabica A\'s balanced, chocolatey profile.',
      },
      {
        heading: 'The Golden Ratio',
        text: 'A universal starting point: 1:16 coffee to water (e.g., 20g coffee to 320ml water). For stronger cups, go 1:15. For lighter, more nuanced extraction, try 1:17. Always use a scale. The single biggest upgrade most home brewers can make is measuring by weight rather than volume.',
      },
      {
        heading: 'Water Quality & Temperature',
        text: 'The ideal water temperature is 195–205°F (90–96°C). Boiling water (212°F) over-extracts, creating bitterness. Too cool, and you\'ll under-extract, producing weak, sour cups. Use filtered water with mineral content between 75–250 ppm TDS for the best results. Avoid distilled or heavily softened water.',
      },
    ],
  },
];

export default function ProcessPage() {
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    document.title = 'Our Process | Farm to Cup Journey | Savana Sips';
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('fadeIn');
      });
    }, { threshold: 0.05 });

    document.querySelectorAll('.pp-fade').forEach((el, i) => {
      el.style.transitionDelay = `${i * 0.08}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const s = {
    page: { backgroundColor: '#FAF7F2', paddingTop: '100px' },
    pageHero: {
      position: 'relative', padding: '6rem 6% 5rem',
      backgroundColor: '#1C1007', overflow: 'hidden', textAlign: 'center',
    },
    heroBg: {
      position: 'absolute', inset: 0,
      backgroundImage: `url('${assets.imgProcess}')`,
      backgroundSize: 'cover', backgroundPosition: 'center',
      opacity: 0.18,
    },
    heroContent: { position: 'relative', zIndex: 1 },
    breadcrumb: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '0.5rem', marginBottom: '1.5rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', color: 'rgba(250,247,242,0.5)',
    },
    pageTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2.8rem, 6vw, 5rem)',
      color: '#FAF7F2', lineHeight: 1.08, marginBottom: '1.25rem',
    },
    accentText: { fontStyle: 'italic', color: '#D96D29' },
    pageSubtitle: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1.05rem', color: 'rgba(250,247,242,0.7)',
      lineHeight: 1.7, maxWidth: '560px', margin: '0 auto',
    },
    // Nav tabs
    stepsNav: {
      display: 'flex', borderBottom: '1px solid #EDE5DC',
      backgroundColor: '#fff', overflowX: 'auto',
      position: 'sticky', top: '72px', zIndex: 50,
      scrollbarWidth: 'none',
    },
    stepTab: (active) => ({
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: '0.3rem',
      padding: '1.25rem 1.5rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em',
      textTransform: 'uppercase', whiteSpace: 'nowrap',
      color: active ? '#A63C1E' : '#8B5E3C',
      background: 'none', border: 'none',
      borderBottom: active ? '2px solid #A63C1E' : '2px solid transparent',
      cursor: 'pointer', transition: 'all 0.3s ease',
    }),
    main: { maxWidth: '1000px', margin: '0 auto', padding: '5rem 6%' },
    sectionBlock: (color) => ({
      display: 'grid', gridTemplateColumns: '1fr 1fr',
      gap: '4rem', marginBottom: '6rem', alignItems: 'start',
    }),
    sectionLeft: {},
    sectionNum: { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', color: '#D96D29', marginBottom: '0.75rem' },
    sectionTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '2.5rem', color: '#1C1209', lineHeight: 1.1,
      marginBottom: '0.4rem',
    },
    sectionSub: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: '#A0693A', marginBottom: '2rem',
    },
    contentItem: { marginBottom: '2rem' },
    contentHeading: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.35rem', color: '#1C1209', marginBottom: '0.75rem',
    },
    contentText: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.95rem', lineHeight: 1.8, color: '#3A2010',
    },
    sectionImg: {
      width: '100%', aspectRatio: '3/4', objectFit: 'cover',
      position: 'sticky', top: '160px',
    },
    divider: { height: '1px', backgroundColor: '#EDE5DC', margin: '0 0 6rem' },
  };

  return (
    <div style={s.page} className="page-enter">
      <div style={s.pageHero}>
        <div style={s.heroBg} aria-hidden="true" />
        <div style={s.heroContent}>
          <div style={s.breadcrumb}>
            <Link to="/" style={{ color: 'rgba(250,247,242,0.45)', textDecoration: 'none' }}>Home</Link>
            <span>›</span>
            <span>Our Process</span>
          </div>
          <h1 style={s.pageTitle}>
            Farm to <span style={s.accentText}>Cup</span>
          </h1>
          <p style={s.pageSubtitle}>
            Discover the artistry, science, and tradition behind every cup —
            from the volcanic soils of Mt. Kenya to your morning ritual.
          </p>
        </div>
      </div>

      {/* Sticky Steps Nav */}
      <div style={s.stepsNav} role="navigation" aria-label="Process sections">
        {sections.map((sec, i) => (
          <button key={sec.id}
            style={s.stepTab(activeSection === i)}
            onClick={() => {
              setActiveSection(i);
              document.getElementById(`section-${sec.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            <span>{sec.icon}</span>
            <span>{sec.num} {sec.title}</span>
          </button>
        ))}
      </div>

      {/* Sections */}
      <div style={s.main}>
        {sections.map((sec, i) => (
          <div key={sec.id} id={`section-${sec.id}`} className="pp-fade preFade">
            <div style={{ ...s.sectionBlock(sec.color), gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              <div style={s.sectionLeft}>
                <div style={s.sectionNum}>{sec.num}</div>
                <h2 style={s.sectionTitle}>{sec.title}</h2>
                <div style={s.sectionSub}>{sec.subtitle}</div>
                {sec.content.map((c, j) => (
                  <div key={j} style={s.contentItem}>
                    <h3 style={s.contentHeading}>{c.heading}</h3>
                    <p style={s.contentText}>{c.text}</p>
                  </div>
                ))}
              </div>
              <div>
                <img src={sec.img} alt={sec.title} style={s.sectionImg} loading="lazy" />
              </div>
            </div>
            {i < sections.length - 1 && <div style={s.divider} />}
          </div>
        ))}

        <div className="pp-fade preFade" style={{ textAlign: 'center', paddingTop: '2rem' }}>
          <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.5rem', fontStyle: 'italic', color: '#8B4224', marginBottom: '2rem' }}>
            "Now that you know the story behind your cup, taste the difference."
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/products" className="btn-primary">Shop Our Coffee →</Link>
            <Link to="/coffee-types" className="btn-secondary">Explore Coffee Types</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
